/**
 * Receiver-side file-transfer resume state — CRUD over
 * `ReflectoStorage.ftReceiverStates` (docs/file-transfer-resume.md §3.1.2).
 * Web's `webStorage` (IndexedDB) mirror of
 * `apps/extension/src/filetransfer/receiverState.ts` — exact field/behavior
 * parity, swapped onto `webStorage.get`/`webStorage.set`.
 *
 * SECURITY: `manifestJson` (and by extension any record) is never logged —
 * security.md.
 */

import {
  FT_STATE_MAX_RECORDS,
  FT_STATE_RETENTION_MS,
  type FtReceiverStateRecord,
  isTerminalReceiverStatus,
} from "@reflecto/client-core";
import { webStorage } from "../adapters/storage.web";

let writeQueue: Promise<void> = Promise.resolve();

function enqueueWrite<T>(fn: () => Promise<T>): Promise<T> {
  const result = writeQueue.then(fn, fn);
  writeQueue = result.then(
    () => undefined,
    () => undefined
  );
  return result;
}

async function readMap(): Promise<Record<string, FtReceiverStateRecord>> {
  const { ftReceiverStates } = await webStorage.get("ftReceiverStates");
  return ftReceiverStates ?? {};
}

/** Persist `record` (full replace, keyed by `record.transferId`). P-R1/P-R2/P-R3/P-R4 all call through this. */
export function saveReceiverState(record: FtReceiverStateRecord): Promise<void> {
  return enqueueWrite(async () => {
    const map = await readMap();
    map[record.transferId] = record;
    await webStorage.set({ ftReceiverStates: map });
  });
}

/**
 * Build a pre-accept terminal tombstone (§8.3) — written BEFORE a
 * decline/cancel ctrl so a crash between the two can never re-notify a
 * decided offer, and a later offer replay stays silent (durable dedup).
 */
export function makePreAcceptTombstone(
  transferId: string,
  peerDeviceId: string,
  status: "declined" | "cancelled",
  now: number = Date.now()
): FtReceiverStateRecord {
  return {
    v: 1,
    transferId,
    peerDeviceId,
    peerPub: "",
    manifestJson: "",
    partCount: 0,
    partsDownloaded: [],
    chunksVerified: 0,
    status,
    acceptedAt: 0,
    expiresAt: 0,
    createdAt: now,
    updatedAt: now,
  };
}

/** Plain read — no §2.4 validation (see module doc). */
export async function getReceiverState(
  transferId: string
): Promise<FtReceiverStateRecord | undefined> {
  const map = await readMap();
  return map[transferId];
}

/** Plain read of every record — no §2.4 validation (see module doc). */
export async function listReceiverStates(): Promise<FtReceiverStateRecord[]> {
  const map = await readMap();
  return Object.values(map);
}

/** Delete one record. No-op if `transferId` is unknown. */
export function removeReceiverState(transferId: string): Promise<void> {
  return enqueueWrite(async () => {
    const map = await readMap();
    if (!(transferId in map)) return;
    delete map[transferId];
    await webStorage.set({ ftReceiverStates: map });
  });
}

/**
 * Lazy tombstone prune (§8.5): drop terminal records older than
 * `FT_STATE_RETENTION_MS` (7 days), then — if more than
 * `FT_STATE_MAX_RECORDS` (50) terminal records remain — drop the oldest
 * terminal ones beyond the cap. Active records are NEVER pruned.
 */
export function pruneReceiverStates(now: number = Date.now()): Promise<void> {
  return enqueueWrite(async () => {
    const map = await readMap();
    const cutoff = now - FT_STATE_RETENTION_MS;

    for (const [id, rec] of Object.entries(map)) {
      if (isTerminalReceiverStatus(rec.status) && rec.updatedAt < cutoff) {
        delete map[id];
      }
    }

    const terminalIds = Object.entries(map)
      .filter(([, rec]) => isTerminalReceiverStatus(rec.status))
      .sort(([, a], [, b]) => a.updatedAt - b.updatedAt)
      .map(([id]) => id);

    const overflow = terminalIds.length - FT_STATE_MAX_RECORDS;
    if (overflow > 0) {
      for (const id of terminalIds.slice(0, overflow)) {
        delete map[id];
      }
    }

    await webStorage.set({ ftReceiverStates: map });
  });
}
